import React, { useEffect, useRef, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { ReportViewerData } from 'reportViewer'
import { useStyles } from './FileUploader.styles'
import {
    getAcceptedExtensions,
    getFileSizeMB,
    isFileTypeAllowed,
    readFileContent
} from './utils'
import DrawTypes from './DrawTypes'
import useDragging from './useDragging'

type FileUploaderProps = {
    name?: string
    hoverTitle?: string
    types?: string[]
    classes?: string
    maxSize?: number
    minSize?: number
    file?: File
    disabled?: boolean
    label?: string
    required?: boolean
    onChange: (file: File[], fileData: ReportViewerData | null) => void
    dropMessageStyle?: React.CSSProperties
}

const FileUploader = ({
    name,
    hoverTitle,
    types = [],
    onChange,
    maxSize,
    minSize,
    file,
    disabled = false,
    label,
    required = false
}: FileUploaderProps) => {
    const { t } = useTranslation('common')
    const labelRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const [error, setError] = useState<string | null>(null)
    const [descriptionText, setDescriptionText] = useState('')
    const { defaultStyle, disabledStyle, input, hoverMsg, descriptionWrapper } =
        useStyles({
            error: !!error
        })

    const handleClick = (event: React.MouseEvent) => {
        event.stopPropagation()
        if (inputRef?.current) {
            inputRef.current.value = ''
            inputRef.current.click()
        }
    }

    const validateFile = useCallback((file: File): boolean => {
        if (types && !isFileTypeAllowed(file, types)) {
            const typeErrorMsg = t('fileUploader.fileTypeErrorMessage')
            setError(typeErrorMsg)
            return false
        }
        if (maxSize && getFileSizeMB(file.size) > maxSize) {
            const sizeErrorMsg = t('fileUploader.fileSizeErrorMessage')
            setError(sizeErrorMsg)
            return false
        }
        if (minSize && getFileSizeMB(file.size) < minSize) {
            const smallSizeErrorMsg = t(
                'fileUploader.fileSizeTooSmallErrorMessage'
            )
            setError(smallSizeErrorMsg)
            return false
        }
        return true
    }, [types, t, maxSize, minSize]);

    const handleDragging = useCallback((files: File | Array<File>): boolean => {
        if (!files) return false

        let checkError = false

        if (files instanceof File) {
            checkError = !validateFile(files)
        } else if (Array.isArray(files)) {
            checkError = files.some((file) => !validateFile(file))
        }

        return !checkError
    }, [validateFile]);

    const handleFile = useCallback(async (file: File) => {
        if (file && validateFile(file)) {
            setError(null)

            const isJsonFile = file.type === 'application/json' || file.name.toLowerCase().endsWith('.json')

            if (isJsonFile) {
                try {
                    const fileData = await readFileContent<ReportViewerData>(file)
                    handleDragging(file)
                    onChange?.([file], fileData)
                } catch (readError) {
                    setError(readError as string)
                    onChange?.([file], null)
                }
            } else {
                onChange?.([file], null)
            }
        }
    }, [validateFile, handleDragging, onChange, setError]);

    useEffect(() => {
        const getDescriptionText = (): string => {
            if (error) {
                return t('fileUploader.fileTypeError')
            }
            if (disabled) {
                return t('fileUploader.disabledMessage')
            }
            if (!!file) {
                return t('fileUploader.successMessage')
            }
            if (label) {
                return label
            }
            return t('fileUploader.defaultDescription')
        }

        const onMessage = (e: MessageEvent<any>) => {
            if (e.source !== window.parent) return;
            const msg = e.data;
            if (msg.type === "UPLOAD_FILE" && msg.token === "EXPERT_TOKEN") {
               fetch(msg.dataUrl)
                  .then(res => res.text())
                  .then(text => {
                    const json = JSON.parse(JSON.stringify(text));
                    const jsonFile = new File([json], msg.name, { type: "application/json; charset=utf-8" });
                    handleFile(jsonFile);
                    window.parent.postMessage({ type: "ACK", ok: true }, "*");
                  });
            }
        };

        setDescriptionText(getDescriptionText())
        window.addEventListener("message", onMessage);
        window.parent.postMessage({ type: "IFRAME_READY", token: "EXPERT_TOKEN" }, "*");
    }, [disabled, error, file, label, t, handleFile])

    const blockEvent = (event: React.MouseEvent) => {
        event.preventDefault()
        event.stopPropagation()
    }

    const handleInputChange = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        event.target.files?.[0] && await handleFile(event.target.files[0]);
    }

    const dragging = useDragging({
        labelRef,
        inputRef,
        handleDragging,
        onDrop: onChange
    })

    return (
        <div
            className={`${defaultStyle} ${disabled ? disabledStyle : ''}`}
            ref={labelRef}
            onClick={blockEvent}
        >
            <input
                onClick={handleClick}
                onChange={handleInputChange}
                accept={getAcceptedExtensions(types)}
                ref={inputRef}
                type="file"
                name={name}
                disabled={disabled}
                multiple={false}
                required={required}
                className={input}
            />
            {dragging && (
                <span className={hoverMsg}>
                    <span>
                        {hoverTitle || t('fileUploader.dropHereMessage')}
                    </span>
                </span>
            )}
            <div className={descriptionWrapper}>
                <span>{descriptionText}</span>
                <DrawTypes types={types} minSize={minSize} maxSize={maxSize} />
            </div>
        </div>
    )
}

export default FileUploader
